import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { Fingerprint, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 60 * 1000; // 1-minute warning before timeout

export function HipaaSessionTimeout() {
  const { isAuthenticated, signOut } = useAuthContext();
  const [state, setState] = useState<'active' | 'warning' | 'expired'>('active');
  const [countdown, setCountdown] = useState(60);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (state !== 'active') {
      setState('active');
      setCountdown(60);
    }
  }, [state]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => {
      if (state === 'active') {
        lastActivityRef.current = Date.now();
      }
    };

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, [isAuthenticated, state]);

  // Check idle status
  useEffect(() => {
    if (!isAuthenticated) return;

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;

      if (elapsed >= IDLE_TIMEOUT_MS) {
        setState('expired');
      } else if (elapsed >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        setState('warning');
        const remaining = Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000);
        setCountdown(Math.max(0, remaining));
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isAuthenticated]);

  const handleReAuthenticate = () => {
    resetTimer();
  };

  const handleSignOut = async () => {
    await signOut();
    setState('active');
  };

  if (!isAuthenticated || state === 'active') return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Blurred backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-xl" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-6 max-w-md mx-4 text-center">
        {/* Biometric icon */}
        <div
          className={cn(
            'flex items-center justify-center w-24 h-24 rounded-full border-2',
            state === 'warning'
              ? 'border-warning/50 bg-warning/10'
              : 'border-destructive/50 bg-destructive/10'
          )}
        >
          {state === 'expired' ? (
            <Lock className="w-10 h-10 text-destructive" />
          ) : (
            <Fingerprint className="w-10 h-10 text-warning animate-pulse" />
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-foreground">
            {state === 'expired'
              ? 'Session Expired'
              : 'Session Timeout Warning'}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {state === 'expired'
              ? 'Re-authenticate for HIPAA Compliance. Your session was terminated after 15 minutes of inactivity per §164.312(a)(2)(iii).'
              : `Your session will expire in ${countdown} seconds due to inactivity. Move your mouse or interact to stay active.`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {state === 'expired' ? (
            <>
              <Button onClick={handleReAuthenticate} className="w-full gap-2">
                <Fingerprint className="w-4 h-4" />
                Re-Authenticate
              </Button>
              <Button variant="outline" onClick={handleSignOut} className="w-full">
                Sign Out
              </Button>
            </>
          ) : (
            <Button onClick={resetTimer} variant="outline" className="w-full gap-2">
              <Fingerprint className="w-4 h-4" />
              I'm Still Here
            </Button>
          )}
        </div>

        {/* Compliance footer */}
        <p className="text-[10px] text-muted-foreground font-mono">
          HIPAA §164.312(a)(2)(iii) · Automatic Logoff
        </p>
      </div>
    </div>
  );
}
